import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, Dispatch, RefObject, SetStateAction } from 'react';
import { pushOutsideClickLayer } from './outside-click-stack';
import { pushEscapeLayer } from '../ui/escape-stack';
import { useAnchoredPosition } from './contact/useAnchoredPosition';

/**
 * Shared open/outside-click/Escape/geometry chrome for a `document.body`-
 * portaled dropdown — the Unit 3 "promote at the 3rd copy" consolidation
 * (`plans/2026-09-22-contact-actions-intelligence-implementation-plan.md`,
 * §1 D9 / §4). This one hook replaces five hand-rolled copies of the same
 * pattern: `ContactActionMenus.tsx`'s private `useDropdown()`,
 * `BulkEnrollPopover.tsx`'s private `useDropdown(canClose)`,
 * `filters/useDropdownAnchor.ts` (now a thin wrapper over this),
 * `plays/PlayRowActionsMenu.tsx`'s inline geometry + listener effects, and
 * `mini-apps/playhouse/components/CampaignRowActionsMenu.tsx`'s identical
 * inline effects (re-exported to mini-apps via
 * `mini-apps/_shared/hooks/usePortalDropdown.ts`).
 *
 * Internals:
 *  - open state is a plain `useState` UNLESS `opts.open` is provided
 *    (controlled mode — the parent owns `open`/`onOpenChange`, e.g. a table
 *    row menu where only one row's menu may be open at a time). In
 *    controlled mode this hook holds NO competing internal state — `setOpen`
 *    is just `onOpenChange` — so the prop is the only source of truth.
 *  - outside-click via `pushOutsideClickLayer` (`./outside-click-stack.ts`)
 *    — only the TOPMOST registered layer evaluates a given outside click, so
 *    two dropdowns open at once (a menu-of-menus is not a supported UI today,
 *    but nothing stops two INDEPENDENT dropdowns from both being open) close
 *    one at a time, most-recently-opened first — a behavior improvement that
 *    comes for free from the shared stack, not a regression from the old
 *    per-component raw listeners (which evaluated independently and could
 *    both close on the same click).
 *  - Escape via `pushEscapeLayer` (`../ui/escape-stack.ts`), same layering.
 *  - `canClose` (read through a ref, not an effect dependency) gates BOTH —
 *    `BulkEnrollPopover`'s in-flight commit guard needs the check made
 *    fresh, at the MOMENT of the dismiss attempt, not baked into a stale
 *    listener from an earlier render. A ref (updated every render, same
 *    pattern as `useAnchoredPosition`'s own `onCloseRef`) reads correctly
 *    even for a dismiss that arrives in the SAME tick as a state change that
 *    hasn't re-rendered yet (see `BulkEnrollPopover`'s Gong-browse-panel
 *    Escape race — the popover's own `canClose` closure must still read the
 *    PRE-update `browsePanelOpen` value when `GongFlowBrowse`'s own
 *    non-stack `onKeyDown` fires first in the same native event bubble).
 *  - `closeOnScroll` (default false) closes on any outside scroll/resize —
 *    the row-menu behavior (`PlayRowActionsMenu`/`CampaignRowActionsMenu`):
 *    the panel is fixed-positioned and computed once on open, so a scroll of
 *    the table's own `overflow-y-auto` scroller moves the trigger's row out
 *    from under a menu that doesn't move — closing (rather than
 *    repositioning) is the simplest correct fix. A scroll that originates
 *    INSIDE the menu itself (the item list scrolling internally) is ignored.
 *    Independent of `useAnchoredPosition`'s own scroll/resize listeners
 *    (which REPOSITION rather than close, and close outright only if the
 *    trigger itself scrolls fully out of view) — both may run on the same
 *    scroll event; harmless, since the menu is about to be closed either way
 *    when `closeOnScroll` is set.
 *  - `swallowDismissClick` (default false) is returned verbatim as the
 *    outside-click layer's return value on a close — `true` tells
 *    `outside-click-stack.ts` to cancel the dismissing click entirely (never
 *    reaching whatever it landed on), matching `filters/useDropdownAnchor`'s
 *    existing behavior; `false` (the row-menu / contact-card default) lets
 *    the click reach its real target normally.
 *  - geometry via `useAnchoredPosition` (`./contact/useAnchoredPosition.ts`),
 *    `align` threaded straight through (default `'left'`).
 */
export interface UsePortalDropdownOptions {
  /** Controlled mode: the parent owns `open`. Omit for uncontrolled (the hook owns its own `useState`). */
  open?: boolean;
  /** Required in controlled mode — called instead of any internal state change. */
  onOpenChange?: (open: boolean) => void;
  /** Gates outside-click and Escape (not the trigger's own click, which the caller wires itself). Re-read fresh on every dismiss attempt. Default: always allowed. */
  canClose?: () => boolean;
  /** Close on any outside scroll/resize (row menus). Default false. */
  closeOnScroll?: boolean;
  /** Horizontal anchor side — see `useAnchoredPosition`'s `AnchorInput.align`. Default 'left'. */
  align?: 'left' | 'right';
  /** Return value of the outside-click layer on a close — `true` swallows the dismissing click. Default false. */
  swallowDismissClick?: boolean;
}

export interface UsePortalDropdownResult<T extends HTMLElement, M extends HTMLElement> {
  open: boolean;
  /**
   * Matches `Dispatch<SetStateAction<boolean>>` (the raw `useState` setter
   * shape) — several migrated call sites toggle via the functional-updater
   * form (`setOpen(o => !o)`), which the old per-component `useState`-backed
   * `useDropdown()` helpers supported natively. In controlled mode the
   * updater is resolved against the current `open` PROP (not stale internal
   * state) before being handed to `onOpenChange`.
   */
  setOpen: Dispatch<SetStateAction<boolean>>;
  triggerRef: RefObject<T | null>;
  menuRef: RefObject<M | null>;
  /** From `useAnchoredPosition` — a fixed-position style for the portaled menu, hidden when closed. */
  menuStyle: CSSProperties;
}

export function usePortalDropdown<T extends HTMLElement = HTMLDivElement, M extends HTMLElement = HTMLDivElement>(
  opts: UsePortalDropdownOptions = {},
): UsePortalDropdownResult<T, M> {
  const { open: controlledOpen, onOpenChange, canClose, closeOnScroll = false, align = 'left', swallowDismissClick = false } = opts;
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;

  const triggerRef = useRef<T>(null);
  const menuRef = useRef<M>(null);

  // Read fresh at call time so the functional-updater form below resolves
  // against the CURRENT controlled `open` prop, not a value captured when
  // this callback was created.
  const controlledOpenRef = useRef(controlledOpen);
  controlledOpenRef.current = controlledOpen;

  const setOpen = useCallback<Dispatch<SetStateAction<boolean>>>(
    (next) => {
      if (isControlled) {
        const resolved = typeof next === 'function' ? next(controlledOpenRef.current ?? false) : next;
        onOpenChange?.(resolved);
      } else {
        setInternalOpen(next);
      }
    },
    [isControlled, onOpenChange],
  );

  // Read fresh at call time (not baked into the effect's closure) — see the
  // module doc's `canClose` note for the race this covers.
  const canCloseRef = useRef(canClose);
  canCloseRef.current = canClose;
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;

  useEffect(() => {
    if (!open) return;
    return pushOutsideClickLayer((target) => {
      if (canCloseRef.current && !canCloseRef.current()) return false;
      const inTrigger = triggerRef.current?.contains(target) ?? false;
      const inMenu = menuRef.current?.contains(target) ?? false;
      if (inTrigger || inMenu) return false;
      setOpenRef.current(false);
      return swallowDismissClick;
    });
  }, [open, swallowDismissClick]);

  useEffect(() => {
    if (!open) return;
    return pushEscapeLayer(() => {
      if (canCloseRef.current && !canCloseRef.current()) return;
      setOpenRef.current(false);
    });
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnScroll) return;
    function onScroll(e: Event) {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      if (canCloseRef.current && !canCloseRef.current()) return;
      setOpenRef.current(false);
    }
    // Capture phase so a scroll on any ancestor overflow container (not just
    // window) is caught — mirrors the row-menus' original listener.
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, closeOnScroll]);

  const menuStyle = useAnchoredPosition(triggerRef, menuRef, open, () => setOpenRef.current(false), align);

  return { open, setOpen, triggerRef, menuRef, menuStyle };
}
